import { PickersActionBarAction } from '@mui/x-date-pickers';

import {
  LocationAttributeCreate,
  Change_Source,
  HoursOfOperationUpdate,
  Weekday,
} from '@occc/graphql';
import { ShowErrorFunc } from '@occc/ui-forms';
import { formatDateTime, DateTimeFormat } from '@occc-app/lib/date';
import {
  WEEKDAY,
  WEEKDAY_SEQUENCE,
} from '@occc-app/lib/util-location-schedule/location-schedule.types';

import {
  RetailerNetworkLocationUpdateFormValues,
  ScheduleValues,
  LOCATION_ATTRIBUTE_VALUE,
} from './retailer-network-location-update-page.types';

export const prepareFormInputs = (
  locationId: string,
  values: RetailerNetworkLocationUpdateFormValues,
) => {
  if (!values.details.state) {
    return null;
  }

  const attributes: LocationAttributeCreate[] = [];
  if (values.details.availableServices && values.details.availableServices.length) {
    values.details.availableServices.forEach((attr) => {
      attributes.push({ name: attr.toString(), value: LOCATION_ATTRIBUTE_VALUE.YES });
    });
  }

  return {
    variables: {
      locationUpdateInput: {
        uuid: locationId,
        changeSource: Change_Source.CommandCenter,
        name: values.details.name,
        phoneNumbers: values.details.phoneNumber ? [values.details.phoneNumber] : [],
        address: {
          street1: values.details.street1,
          street2: values.details.street2 || null,
          city: values.details.city,
          zipCode: values.details.zipCode,
          state: values.details.state.id,
          geoLocation:
            values.geoLocation && values.geoLocation.latitude && values.geoLocation.longitude
              ? {
                  latitude: parseFloat(values.geoLocation.latitude.toString()),
                  longitude: parseFloat(values.geoLocation.longitude.toString()),
                }
              : {
                  // API sets `geoLocation` to null when lat and long is 0
                  latitude: 0,
                  longitude: 0,
                },
        },
        attributes,
        hoursOfOperationGroup: getOperationHoursFromFormValues(
          values.schedule,
          values.availableDays,
        ),
        brandingDetails:
          values.details.brandingDetails?.primaryColor ||
          values.details.brandingDetails?.secondaryColor
            ? {
                primaryColor: values.details.brandingDetails?.primaryColor || null,
                secondaryColor: values.details.brandingDetails?.secondaryColor || null,
              }
            : null,
      },
    },
  };
};

export const clearTime = (input: string) => (input === '24:00' ? '00:00' : input);

const mapDayNameToWeekday: Record<string, Weekday> = {
  FRIDAY: Weekday.Friday,
  MONDAY: Weekday.Monday,
  SATURDAY: Weekday.Saturday,
  SUNDAY: Weekday.Sunday,
  THURSDAY: Weekday.Thursday,
  TUESDAY: Weekday.Tuesday,
  WEDNESDAY: Weekday.Wednesday,
};

export const getOperationHoursFromFormValues = (
  schedule: ScheduleValues,
  availableDays: string[],
) => {
  const hours = Object.entries(schedule).reduce<HoursOfOperationUpdate[]>((acc, [day, hrs]) => {
    const isLocationClosedAllDay = !availableDays.includes(day);
    const isOperationTimeSet = hrs && hrs.start && hrs.end;
    const isLocationOpenedAllDay = hrs && hrs.isOpenAllDay;

    if (isLocationClosedAllDay || (!isOperationTimeSet && !isLocationOpenedAllDay)) {
      return acc;
    }

    const dayOfWeek = mapDayNameToWeekday[day];

    if (hrs.isOpenAllDay) {
      return [
        ...acc,
        {
          dayOfWeek,
          openTime: '00:00',
          closeTime: '00:00',
        },
      ];
    }

    return [
      ...acc,
      {
        dayOfWeek,
        openTime: formatDateTime(hrs.start, DateTimeFormat.Time24hShort),
        closeTime: formatDateTime(hrs.end, DateTimeFormat.Time24hShort),
      },
    ];
  }, []);

  return { hours: hours || [] };
};

type PickerConfiguration = { actionBar: { actions: PickersActionBarAction[] } };

export const pickerSlotConfiguration: PickerConfiguration = {
  actionBar: { actions: ['accept', 'clear'] },
};

export const showErrorWhenInvalid: ShowErrorFunc = ({ meta: { error, submitError } }) =>
  !!(error || submitError);

export const weekdayChipGroupOptions = WEEKDAY_SEQUENCE.map((weekday) => ({
  label: weekday,
  value: weekday,
}));

export const sortToWeekdaySequence = (days: WEEKDAY[]): string[] => {
  return days.sort((dayA, dayB) => {
    const dayAPosition = WEEKDAY_SEQUENCE.indexOf(dayA);
    const dayBPosition = WEEKDAY_SEQUENCE.indexOf(dayB);

    return dayAPosition - dayBPosition;
  });
};
